import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../hooks/useTheme';
import { discountsAPI, clothingDiscountsAPI, configAPI } from '../api/client';
import { useAuthStore } from '../store';
import { formatKyivDateTime } from '../utils/kyivTime';
import '../styles/settings.css';

const THEME_OPTIONS = [
  { value: 'light', icon: '☀️', label: 'Светлая' },
  { value: 'dark',  icon: '🌙', label: 'Тёмная'  },
  { value: 'auto',  icon: '⚙️', label: 'Авто'    },
];

// ── Discount section ──────────────────────────────────────────────────────────

const DiscountRow = ({ discount, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(discount.discount_amount));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const num = parseFloat(val);
    if (isNaN(num) || num <= 0) return;
    setSaving(true);
    await onSave(discount.id, num);
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="discount-row">
      <span className="discount-supplier">{discount.supplier_name_norm}</span>
      {editing ? (
        <div className="discount-edit-inline">
          <input
            className="discount-percent-input"
            type="number"
            min="1"
            step="1"
            value={val}
            onChange={e => setVal(e.target.value.replace(',', '.'))}
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
            autoFocus
          />
          <span className="discount-pct-sign">₴</span>
          <button className="discount-btn discount-btn--save" onClick={handleSave} disabled={saving}>✓</button>
          <button className="discount-btn discount-btn--cancel" onClick={() => { setVal(String(discount.discount_amount)); setEditing(false); }}>✕</button>
        </div>
      ) : (
        <div className="discount-actions">
          <span className="discount-badge">−{discount.discount_amount} ₴</span>
          <button className="discount-btn discount-btn--edit" onClick={() => setEditing(true)}>Изм.</button>
          <button className="discount-btn discount-btn--delete" onClick={() => onDelete(discount.id)}>Удал.</button>
        </div>
      )}
    </div>
  );
};

const AddDiscountForm = ({ suppliers, existingNorms, onAdd, onClose }) => {
  const [supplier, setSupplier] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const available = suppliers.filter(s => !existingNorms.has(s));
  const filtered = search
    ? available.filter(s => s.toLowerCase().includes(search.toLowerCase()))
    : available;

  const handleAdd = async () => {
    setError('');
    if (!supplier) { setError('Выберите поставщика'); return; }
    const num = parseFloat(amount);
    if (isNaN(num) || num <= 0) { setError('Введите сумму скидки'); return; }
    setSaving(true);
    try {
      await onAdd(supplier, num);
      onClose();
    } catch {
      setError('Ошибка при сохранении');
      setSaving(false);
    }
  };

  return (
    <div className="discount-add-form">
      <div className="discount-supplier-wrap">
        <input
          className="discount-search-input"
          type="text"
          placeholder="Поиск поставщика..."
          value={search}
          onChange={e => { setSearch(e.target.value); setSupplier(''); }}
          onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
          autoFocus
        />
        <select
          className="discount-supplier-select"
          value={supplier}
          onChange={e => setSupplier(e.target.value)}
          size={search ? Math.min(Math.max(filtered.length, 1), 7) : 1}
        >
          <option value="">— выберите —</option>
          {filtered.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {search && filtered.length === 0 && (
          <div className="discount-search-empty">Поставщик не найден</div>
        )}
      </div>
      <div className="discount-percent-wrap">
        <input
          className="discount-percent-input"
          type="number"
          min="1"
          step="1"
          placeholder="0"
          value={amount}
          onChange={e => setAmount(e.target.value.replace(',', '.'))}
          onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') onClose(); }}
        />
        <span className="discount-pct-sign">₴</span>
      </div>
      <button className="discount-btn discount-btn--add" onClick={handleAdd} disabled={saving || !supplier}>
        {saving ? '...' : 'Добавить'}
      </button>
      <button className="discount-btn discount-btn--cancel" onClick={onClose}>✕</button>
      {error && <span className="discount-error">{error}</span>}
    </div>
  );
};

const DiscountsSection = () => {
  const [discounts, setDiscounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    const [dRes, sRes] = await Promise.allSettled([discountsAPI.list(), discountsAPI.getSuppliers()]);
    if (dRes.status === 'fulfilled') setDiscounts(dRes.value.data);
    if (sRes.status === 'fulfilled') setSuppliers(sRes.value.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (supplier_name_norm, discount_amount) => {
    const res = await discountsAPI.create(supplier_name_norm, discount_amount);
    setDiscounts(prev => [...prev.filter(d => d.supplier_name_norm !== supplier_name_norm), res.data]);
  };

  const handleSave = async (id, discount_amount) => {
    const res = await discountsAPI.update(id, discount_amount);
    setDiscounts(prev => prev.map(d => d.id === id ? res.data : d));
  };

  const handleDelete = async (id) => {
    await discountsAPI.remove(id);
    setDiscounts(prev => prev.filter(d => d.id !== id));
  };

  const existingNorms = new Set(discounts.map(d => d.supplier_name_norm));
  const availableSuppliers = suppliers.filter(s => !existingNorms.has(s));

  if (loading) return <div className="discount-loading">Загрузка...</div>;

  return (
    <div className="discounts-section">
      {discounts.length === 0 && !showForm && (
        <p className="discount-empty">Скидки не добавлены</p>
      )}
      {discounts.length > 0 && (
        <div className="discount-list">
          {[...discounts].sort((a, b) => a.supplier_name_norm.localeCompare(b.supplier_name_norm)).map(d => (
            <DiscountRow key={d.id} discount={d} onSave={handleSave} onDelete={handleDelete} />
          ))}
        </div>
      )}
      {showForm ? (
        <AddDiscountForm
          suppliers={suppliers}
          existingNorms={existingNorms}
          onAdd={handleAdd}
          onClose={() => setShowForm(false)}
        />
      ) : (availableSuppliers.length > 0 || suppliers.length === 0) ? (
        <button className="discount-add-trigger" onClick={() => setShowForm(true)}>
          + Добавить скидку
        </button>
      ) : null}
    </div>
  );
};

const ClothingDiscountsSection = () => {
  const [discounts, setDiscounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    const [dRes, sRes] = await Promise.allSettled([clothingDiscountsAPI.list(), clothingDiscountsAPI.getSuppliers()]);
    if (dRes.status === 'fulfilled') setDiscounts(dRes.value.data);
    if (sRes.status === 'fulfilled') setSuppliers(sRes.value.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (supplier_name_norm, discount_amount) => {
    const res = await clothingDiscountsAPI.create(supplier_name_norm, discount_amount);
    setDiscounts(prev => [...prev.filter(d => d.supplier_name_norm !== supplier_name_norm), res.data]);
  };

  const handleSave = async (id, discount_amount) => {
    const res = await clothingDiscountsAPI.update(id, discount_amount);
    setDiscounts(prev => prev.map(d => d.id === id ? res.data : d));
  };

  const handleDelete = async (id) => {
    await clothingDiscountsAPI.remove(id);
    setDiscounts(prev => prev.filter(d => d.id !== id));
  };

  const existingNorms = new Set(discounts.map(d => d.supplier_name_norm));
  const availableSuppliers = suppliers.filter(s => !existingNorms.has(s));

  if (loading) return <div className="discount-loading">Загрузка...</div>;

  return (
    <div className="discounts-section">
      {discounts.length === 0 && !showForm && (
        <p className="discount-empty">Скидки не добавлены</p>
      )}
      {discounts.length > 0 && (
        <div className="discount-list">
          {[...discounts].sort((a, b) => a.supplier_name_norm.localeCompare(b.supplier_name_norm)).map(d => (
            <DiscountRow key={d.id} discount={d} onSave={handleSave} onDelete={handleDelete} />
          ))}
        </div>
      )}
      {showForm ? (
        <AddDiscountForm
          suppliers={suppliers}
          existingNorms={existingNorms}
          onAdd={handleAdd}
          onClose={() => setShowForm(false)}
        />
      ) : (availableSuppliers.length > 0 || suppliers.length === 0) ? (
        <button className="discount-add-trigger" onClick={() => setShowForm(true)}>
          + Добавить скидку
        </button>
      ) : null}
    </div>
  );
};

// ── Audit log section (admin only) ───────────────────────────────────────────

const METHOD_COLOR = {
  GET:    'var(--lg-text-3)',
  POST:   '#2563eb',
  PATCH:  '#d97706',
  DELETE: '#dc2626',
  PUT:    '#7c3aed',
};

const AuditLogSection = () => {
  const [items,   setItems]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset,  setOffset]  = useState(0);
  const LIMIT = 100;

  const load = useCallback(async (off = 0) => {
    setLoading(true);
    try {
      const r = await configAPI.getAuditLog({ limit: LIMIT, offset: off });
      setItems(off === 0 ? r.data.items : prev => [...prev, ...r.data.items]);
      setTotal(r.data.total);
      setOffset(off);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(0); }, [load]);

  function fmt(iso) {
    return formatKyivDateTime(iso, 'uk-UA', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  }

  // Коротший шлях для відображення
  function shortPath(path) {
    return path.replace('/api/v1/admin/', '').replace('/api/v1/auth/admin/', '');
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: 'var(--lg-text-2)' }}>
          {loading ? 'Завантаження…' : `${total} записів`}
        </span>
        <button
          style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, border: '1px solid var(--lg-border)',
                   background: 'var(--lg-surface)', cursor: 'pointer', color: 'var(--lg-text)' }}
          onClick={() => load(0)}
          disabled={loading}
        >Оновити</button>
      </div>

      {items.length === 0 && !loading && (
        <div style={{ color: 'var(--lg-text-3)', fontSize: 13 }}>Записів немає</div>
      )}

      {items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 }}>
            <thead>
              <tr style={{ color: 'var(--lg-text-3)', borderBottom: '1px solid var(--lg-border)' }}>
                <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600 }}>Час</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600 }}>Користувач</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600 }}>Метод</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600 }}>Маршрут</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600 }}>IP</th>
                <th style={{ textAlign: 'center', padding: '4px 8px', fontWeight: 600 }}>Статус</th>
              </tr>
            </thead>
            <tbody>
              {items.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--lg-border)' }}>
                  <td style={{ padding: '4px 8px', whiteSpace: 'nowrap', color: 'var(--lg-text-3)' }}>
                    {fmt(r.ts)}
                  </td>
                  <td style={{ padding: '4px 8px', fontWeight: 500 }}>
                    {r.username}
                    <span style={{ marginLeft: 4, fontSize: 10, color: 'var(--lg-text-3)' }}>#{r.user_id}</span>
                  </td>
                  <td style={{ padding: '4px 8px' }}>
                    <span style={{
                      fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
                      color: METHOD_COLOR[r.method] || 'var(--lg-text)',
                    }}>{r.method}</span>
                  </td>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: 11,
                               color: 'var(--lg-text-2)', maxWidth: 340, overflow: 'hidden',
                               textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={r.path}>
                    {shortPath(r.path)}
                  </td>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: 11,
                               color: 'var(--lg-text-3)' }}>
                    {r.ip || '—'}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                    <span style={{
                      fontSize: 11, padding: '1px 6px', borderRadius: 4,
                      background: r.status_code < 300 ? 'rgba(52,199,89,0.12)'
                                : r.status_code < 500 ? 'rgba(255,149,0,0.12)'
                                : 'rgba(255,59,48,0.12)',
                      color: r.status_code < 300 ? '#34c759'
                           : r.status_code < 500 ? '#c97c00'
                           : '#ff3b30',
                      fontWeight: 600,
                    }}>{r.status_code}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {items.length < total && (
        <button
          style={{ marginTop: 10, fontSize: 12, padding: '4px 14px', borderRadius: 6,
                   border: '1px solid var(--lg-border)', background: 'var(--lg-surface)',
                   cursor: 'pointer', color: 'var(--lg-text)' }}
          disabled={loading}
          onClick={() => load(offset + LIMIT)}
        >Завантажити ще ({total - items.length})</button>
      )}
    </div>
  );
};


// ── Main page ─────────────────────────────────────────────────────────────────

const SettingsPage = ({ onBack, onAdmin }) => {
  const { preference, setTheme } = useTheme();
  const isAdmin = useAuthStore(s => s.user?.is_admin);
  const user = useAuthStore(s => s.user);
  const hasClothingAccess = isAdmin
    || user?.subscription_type === 'Одежда'
    || user?.subscription_type === 'Обувь и Одежда';

  return (
    <div className="settings-page">
      <div className="settings-header">
        <button className="settings-back-btn" onClick={onBack} title="Назад">‹</button>
        <span className="settings-header-title">Настройки</span>
        {isAdmin && (
          <button className="settings-catalog-btn" onClick={onAdmin}>Управление</button>
        )}
        <button className="settings-catalog-btn" onClick={onBack}>Каталог</button>
      </div>

      <div className="settings-body">
        <div className="settings-section">
          <div className="settings-section-title">Внешний вид</div>
          <div className="settings-card">
            <div className="settings-row">
              <div>
                <div className="settings-row-label">Тема</div>
                <div className="settings-row-desc">Авто — следует системным настройкам</div>
              </div>
              <div className="theme-picker">
                {THEME_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    className={`theme-option${preference === opt.value ? ' active' : ''}`}
                    onClick={() => setTheme(opt.value)}
                  >
                    <span className="theme-option-icon">{opt.icon}</span>
                    <span className="theme-option-label">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <div className="settings-section-title">Мои скидки у поставщиков</div>
          <div className="settings-card">
            <DiscountsSection />
          </div>
        </div>

        {hasClothingAccess && (
          <div className="settings-section">
            <div className="settings-section-title">Мои скидки у поставщиков одежды</div>
            <div className="settings-card">
              <ClothingDiscountsSection />
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="settings-section">
            <div className="settings-section-title">🔍 Аудит-лог</div>
            <div style={{ fontSize: 12, color: 'var(--lg-text-3)', marginBottom: 10 }}>
              Хто, що і коли запитував через API. GET — перегляд даних, POST/PATCH/DELETE — зміни.
            </div>
            <div className="settings-card">
              <AuditLogSection />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
